import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { queue, QueueObject } from 'async';

export interface RequestTask extends AxiosRequestConfig {
  callback: (error: any, data: any) => void;
  uri?: string;
  qs?: any;
  body?: any;
  encoding?: string;
}

export interface OptionsWithUri extends AxiosRequestConfig {
  uri?: string;
  qs?: any;
  body?: any;
  encoding?: string;
}

export class RequestClient {
  private concurrency: number = 40;
  private queue: QueueObject<RequestTask>;
  private axios: AxiosInstance;

  constructor() {
    this.axios = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        'Content-Type': 'application/json'
      },
    });

    this.queue = queue<RequestTask, any>(this.requestGenerator(), this.concurrency);
  }

  preprocess(response: AxiosResponse) {
    return {
      headers: response.headers,
      data: response.data
    };
  }

  push(task: RequestTask) {
    this.queue.push(task);
  }

  requestGenerator() {
    return (task: RequestTask, done: Function) => {
      const { callback, uri, qs, body, encoding, ...options } = task;
      
      // If uri is provided, use it as url (for backward compatibility)
      if (uri) {
        options.url = uri;
      }
      
      // If qs is provided, use it as params (for backward compatibility with request-promise)
      if (qs) {
        options.params = qs;
      }
      
      // If body is provided, parse it and use as data (for backward compatibility)
      if (body) {
        try {
          options.data = typeof body === 'string' ? JSON.parse(body) : body;
        } catch {
          options.data = body;
        }
      }
      
      // If encoding is binary, set responseType to arraybuffer
      if (encoding === 'binary') {
        options.responseType = 'arraybuffer';
      }

      this.axios.request(options)
        .then((response) => {
          const processed = this.preprocess(response);
          const contentType = processed.headers['content-type'];

          if (contentType === undefined || contentType.startsWith('application/json')) {
            task.callback(undefined, processed.data || {});
          } else {
            task.callback(undefined, processed.data);
          }

          done();
        })
        .catch(error => {
          // Transform axios error to match old request-promise format
          // This ensures existing error handling in components continues to work
          task.callback(error.response || error, undefined);
          done();
        });
    };
  }
}